// src/App.js
import React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useLocation,
} from 'react-router-dom';

import { AuthProvider as OidcProvider } from 'react-oidc-context';

import { oidcConfig } from './auth/oidcConfig';
import { AuthProvider, useAuth } from './context/AuthContext';

import Login from './components/Login';
import Logout from './components/Logout';
import DeviceList from './components/DeviceList';
import DeviceDetail from './components/DeviceDetails';
import DeviceGroups from './components/DeviceGroups';
import GroupDetail from './components/GroupDetails.js';
import Notifications from './components/Notifications';
import Alarms from './components/AllAlarms.js';
import AllAlarmrules from './components/AllAlarmrules.js';
import DashboardList from './components/DashboardList.js';
import DashboardView from './components/DashboardView.js';
import DashboardGroups from './components/DashboardGroups.js';
import DashboardGroupDetails from './components/DashboardGroupDetails.js';
import NewDashboard from './components/NewDashboard.js';
import AddDevice from './components/AddDevice';
import OverviewAddedDevices from './components/OverviewAddedDevices';
import RegisterDeviceByType from './components/RegisterDeviceByType';
import NewAlarmrule from './components/NewAlarmrule';
import AlarmruleDetail from './components/AlarmruleDetail';
import ExportData from './components/ExportData';
import SendDownlink from './components/SendDownlink';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Home from './components/Home';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

import './App.css';

function AppLayout() {
  const { isLoggedIn, isAuthLoading, handleLogout } = useAuth();
  const location = useLocation();
  // /logout ist der Notausgang und muss ohne Anmeldung erreichbar sein - wer
  // ihn braucht, hat ja gerade keine brauchbare Sitzung mehr.
  const oeffentlich = location.pathname === '/login' || location.pathname === '/logout';
  const hideNavbar = oeffentlich;
  const hideSidebar = oeffentlich || location.pathname === '/home';
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const mainContentRef = React.useRef(null);

  // Scroll to top on route change
  React.useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Close sidebar on route change (mobile)
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (isAuthLoading) {
    return null;
  }

  if (!isLoggedIn && !oeffentlich) {
    return <Navigate to="/login" />;
  }

  return (
    <>
      {!hideNavbar && <Navbar onLogout={handleLogout} onToggleSidebar={() => setSidebarOpen(prev => !prev)} showSidebarToggle={!hideSidebar} />}
      {sidebarOpen && !hideSidebar && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <div className="app-body">
        {!hideSidebar && <Sidebar className={sidebarOpen ? 'sidebar-open' : ''} />}
        <div className="main-content" ref={mainContentRef}>
          <Outlet />
        </div>
      </div>
      <Footer />
    </>
  );
}

function Protected({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function CatchAll() {
  // isReady statt isLoggedIn: Wer angemeldet, aber nicht eingerichtet ist,
  // wuerde sonst nach /home geschickt und von dort sofort wieder zurueck.
  const { isReady } = useAuth();
  return <Navigate to={isReady ? "/home" : "/login"} />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { path: 'login', element: <Login /> },
      { path: 'logout', element: <Logout /> },
      { path: 'home', element: <Protected><Home /></Protected> },
      { path: 'devices', element: <Protected><DeviceList /></Protected> },
      { path: 'add-device', element: <Protected><AddDevice /></Protected> },
      { path: 'overview-added-devices', element: <Protected><OverviewAddedDevices /></Protected> },
      { path: 'register-device-type', element: <Protected><RegisterDeviceByType /></Protected> },
      { path: 'devices/:deviceId', element: <Protected><DeviceDetail /></Protected> },
      { path: 'devices/:deviceId/downlink', element: <Protected><SendDownlink /></Protected> },
      { path: 'device-groups', element: <Protected><DeviceGroups /></Protected> },
      { path: 'device-groups/:groupId', element: <Protected><GroupDetail /></Protected> },
      { path: 'dashboards', element: <Protected><DashboardList /></Protected> },
      { path: 'dashboard/new', element: <Protected><NewDashboard /></Protected> },
      { path: 'dashboard/view/:dashboardId', element: <Protected><DashboardView /></Protected> },
      { path: 'dashboard-groups', element: <Protected><DashboardGroups /></Protected> },
      { path: 'dashboard-groups/:groupId', element: <Protected><DashboardGroupDetails /></Protected> },
      { path: 'notifications', element: <Protected><Notifications /></Protected> },
      { path: 'alarms', element: <Protected><Alarms /></Protected> },
      { path: 'alarm-rules', element: <Protected><AllAlarmrules /></Protected> },
      { path: 'alarm-rules/:ruleId', element: <Protected><AlarmruleDetail /></Protected> },
      { path: 'new-alarm-rule', element: <Protected><NewAlarmrule /></Protected> },
      { path: 'export-data', element: <Protected><ExportData /></Protected> },
      { path: '*', element: <CatchAll /> },
    ],
  },
]);

function App() {
  // Reihenfolge zaehlt: Der eigene AuthProvider liest den Zustand des
  // OIDC-Providers, muss also darin liegen.
  return (
    <OidcProvider {...oidcConfig}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </OidcProvider>
  );
}

export default App;